/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { Lock, Unlock, Save, Printer, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  green:'#7EA594', greenLight:'#EAF2EF',
  amber:'#E07B00', amberLight:'#FFF3E0',
  red:'#DC3545', redLight:'#FFF0F0',
  white:'#FFFFFF', yellow:'#F7EC71',
  purple:'#7C3AED', purpleLight:'#F3EEFF',
};

const iStyle = (disabled) => ({
  width:'100%', padding:'7px 10px', border:`1.5px solid ${disabled ? C.border : C.border}`,
  borderRadius:'8px', fontSize:'13px', boxSizing:'border-box',
  background: disabled ? C.cream : C.white, color: disabled ? C.mid : C.charcoal,
  outline:'none', fontFamily:'inherit', textAlign:'right',
});

function r2(n) { return Math.round(n * 100) / 100; }
function fmt(n) { return n != null ? `€${Number(n).toFixed(2)}` : '—'; }
function pct(n) { return n != null ? `${Number(n).toFixed(1)}%` : '—'; }

// ── Period generation ─────────────────────────────────────────────────
function generatePeriods(count = 24) {
  const periods = [];
  const now = new Date();
  const m = now.getMonth();
  const d = now.getDate();
  let ey = now.getFullYear();
  let em = m;
  if (d > 26) em = m + 1;
  if (em > 11) { em = 0; ey++; }
  for (let i = -(count - 6); i <= 5; i++) {
    let endMonth = em + i; let endYear = ey;
    while (endMonth < 0)  { endMonth += 12; endYear--; }
    while (endMonth > 11) { endMonth -= 12; endYear++; }
    let startMonth = endMonth - 1; let startYear = endYear;
    if (startMonth < 0) { startMonth = 11; startYear--; }
    const pad = n => String(n).padStart(2,'0');
    const pNum = endMonth + 1; const pYear = endYear;
    periods.push({ period_number:pNum, year:pYear, start_date:`${startYear}-${pad(startMonth+1)}-26`, end_date:`${endYear}-${pad(endMonth+1)}-25`, label:`P${pNum} ${pYear}` });
  }
  return periods.sort((a,b) => a.year!==b.year ? b.year-a.year : b.period_number-a.period_number);
}

function getCurrentPeriodLabel() {
  const now = new Date(); const m = now.getMonth(); const d = now.getDate();
  let periodMonth = m; let periodYear = now.getFullYear();
  if (d > 26) periodMonth = m + 1;
  if (periodMonth > 11) { periodMonth = 0; periodYear++; }
  return `P${periodMonth + 1} ${periodYear}`;
}

// ── Venue config ──────────────────────────────────────────────────────
const VENUE_ROWS = [
  { key:'jordaan',  label:'Box Jordaan',  revenueKey:'revenue_jordaan',  cashKey:'tips_jordaan_cash',  eftKey:'tips_jordaan_eft',  hasEft:true,  color:'#2563EB', bg:'#EDF3FC', border:'#BAD4F5' },
  { key:'plantage', label:'Box Plantage', revenueKey:'revenue_plantage', cashKey:'tips_plantage_cash', eftKey:'tips_plantage_eft', hasEft:true,  color:'#2D7A65', bg:'#C8E6D8', border:'#7EA594' },
  { key:'sassys',   label:"Sassy's",      revenueKey:'revenue_sassys',   cashKey:'tips_sassys_cash',   eftKey:null,                hasEft:false, color:'#6D28A0', bg:'#EDE0F7', border:'#6D28A0' },
];

// ── Summary / Input card ──────────────────────────────────────────────
function SummaryCard({ period, members, locked, onChange }) {
  const boxTips     = (period.tips_jordaan_cash||0)+(period.tips_jordaan_eft||0)+(period.tips_plantage_cash||0)+(period.tips_plantage_eft||0);
  const sassysTips  = (period.tips_sassys_cash||0);
  const totalTips   = boxTips + sassysTips;
  const boxHours    = members.reduce((s,m) => s + (m.box_hours||0), 0);
  const sassysHours = members.reduce((s,m) => s + (m.sassys_hours||0), 0);
  const boxRevenue  = (period.revenue_jordaan||0)+(period.revenue_plantage||0);
  const sassysRev   = (period.revenue_sassys||0);
  const totalRev    = boxRevenue + sassysRev;
  const boxTph      = boxHours > 0 ? boxTips / boxHours : 0;
  const sassysTph   = sassysHours > 0 ? sassysTips / sassysHours : 0;

  return (
    <div style={{ background:C.white, borderRadius:'12px', border:`1px solid ${C.border}`, marginBottom:'16px', overflow:'hidden' }}>
      <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontWeight:800, fontSize:'14px', color:C.charcoal }}>Period Inputs</div>
        <div style={{ fontSize:'11px', color:C.mid }}>Revenue &amp; tips by venue</div>
      </div>

      {/* Input table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
          <thead>
            <tr style={{ background:C.cream }}>
              <th style={{ padding:'10px 16px', textAlign:'left',  fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', width:'160px' }}>Venue</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>Revenue (incl. tax)</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>Cash Tips</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>EFT Tips</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>Total Tips</th>
              <th style={{ padding:'10px 16px', textAlign:'right', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em' }}>Tip %</th>
            </tr>
          </thead>
          <tbody>
            {VENUE_ROWS.map((v, i) => {
              const rev      = period[v.revenueKey] || 0;
              const cash     = period[v.cashKey]    || 0;
              const eft      = v.eftKey ? (period[v.eftKey] || 0) : 0;
              const rowTips  = cash + eft;
              const rowPct   = rev > 0 ? (rowTips / rev * 100) : null;
              return (
                <tr key={v.key} style={{ borderBottom:`1px solid ${C.border}`, background: i%2===0 ? C.white : '#FAFAF8' }}>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <span style={{ width:10, height:10, borderRadius:'50%', background:v.color, display:'inline-block', flexShrink:0 }}/>
                      <span style={{ fontWeight:700, fontSize:'13px', color:C.charcoal }}>{v.label}</span>
                    </div>
                  </td>
                  {/* Revenue */}
                  <td style={{ padding:'6px 10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent:'flex-end' }}>
                      <span style={{ fontSize:'12px', color:C.mid }}>€</span>
                      <input type="number" min="0" step="0.01" value={period[v.revenueKey]||''} disabled={locked}
                        onChange={e => onChange(v.revenueKey, parseFloat(e.target.value)||0)} style={{...iStyle(locked), width:'110px'}}/>
                    </div>
                  </td>
                  {/* Cash tips */}
                  <td style={{ padding:'6px 10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent:'flex-end' }}>
                      <span style={{ fontSize:'12px', color:C.mid }}>€</span>
                      <input type="number" min="0" step="0.01" value={period[v.cashKey]||''} disabled={locked}
                        onChange={e => onChange(v.cashKey, parseFloat(e.target.value)||0)} style={{...iStyle(locked), width:'110px'}}/>
                    </div>
                  </td>
                  {/* EFT tips */}
                  <td style={{ padding:'6px 10px' }}>
                    {v.hasEft ? (
                      <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent:'flex-end' }}>
                        <span style={{ fontSize:'12px', color:C.mid }}>€</span>
                        <input type="number" min="0" step="0.01" value={period[v.eftKey]||''} disabled={locked}
                          onChange={e => onChange(v.eftKey, parseFloat(e.target.value)||0)} style={{...iStyle(locked), width:'110px'}}/>
                      </div>
                    ) : (
                      <div style={{ textAlign:'right', padding:'0 10px', fontSize:'12px', color:C.border }}>—</div>
                    )}
                  </td>
                  {/* Total tips */}
                  <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color:rowTips>0?v.color:C.mid, fontSize:'13px' }}>
                    {rowTips > 0 ? fmt(rowTips) : '—'}
                  </td>
                  {/* Tip % */}
                  <td style={{ padding:'10px 16px', textAlign:'right', fontSize:'12px', color:C.mid }}>
                    {rowPct !== null ? pct(rowPct) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Totals row */}
          <tfoot>
            <tr style={{ background:C.cream, borderTop:`2px solid ${C.border}` }}>
              <td style={{ padding:'11px 16px', fontWeight:800, fontSize:'13px', color:C.charcoal }}>TOTAL</td>
              <td style={{ padding:'11px 16px', textAlign:'right', fontWeight:700, color:C.charcoal }}>{totalRev > 0 ? fmt(totalRev) : '—'}</td>
              <td style={{ padding:'11px 16px', textAlign:'right', fontWeight:700, color:C.charcoal }}>
                {fmt((period.tips_jordaan_cash||0)+(period.tips_plantage_cash||0)+(period.tips_sassys_cash||0))}
              </td>
              <td style={{ padding:'11px 16px', textAlign:'right', fontWeight:700, color:C.charcoal }}>
                {fmt((period.tips_jordaan_eft||0)+(period.tips_plantage_eft||0))}
              </td>
              <td style={{ padding:'11px 16px', textAlign:'right', fontWeight:800, color:C.charcoal }}>{fmt(totalTips)}</td>
              <td style={{ padding:'11px 16px', textAlign:'right', fontSize:'12px', color:C.mid }}>
                {totalRev > 0 ? pct(totalTips/totalRev*100) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1px', borderTop:`1px solid ${C.border}`, background:C.border }}>
        {[
          { label:'Box Tips Pool',    value: fmt(boxTips),     sub: `${boxHours.toFixed(1)}h · ${fmt(boxTph)}/hr`,     color:C.skyText,  bg:C.skyBlueLight },
          { label:"Sassy's Pool",     value: fmt(sassysTips),  sub: `${sassysHours.toFixed(1)}h · ${fmt(sassysTph)}/hr`, color:C.purple,  bg:C.purpleLight  },
          { label:'Total Tips',       value: fmt(totalTips),   sub: `${(boxHours+sassysHours).toFixed(1)}h total`,       color:C.charcoal, bg:C.yellow+'44'  },
          { label:'Total Revenue',    value: fmt(totalRev),    sub: totalRev>0?`${pct(totalTips/totalRev*100)} tip rate`:'—', color:C.green, bg:C.greenLight },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, padding:'14px 16px' }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>{s.label}</div>
            <div style={{ fontSize:'20px', fontWeight:900, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:'11px', color:C.mid, marginTop:'3px' }}>{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════
export default function Tips({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';

  const [selectedP,     setSelectedP]     = useState(() => parseInt(getCurrentPeriodLabel().split(' ')[0].replace('P','')));
  const [selectedYear,  setSelectedYear]  = useState(() => parseInt(getCurrentPeriodLabel().split(' ')[1]));
  const [selectedLabel, setSelectedLabel] = useState(getCurrentPeriodLabel());
  const [period,        setPeriod]        = useState(null);
  const [members,       setMembers]       = useState([]);
  const [memberOrder,   setMemberOrder]   = useState({});
  const [loading,       setLoading]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [dirty,         setDirty]         = useState(false);
  const [vf,            setVf]            = useState('All');
  const [lastSynced,    setLastSynced]    = useState(null);

  const generatedPeriods = useMemo(() => generatePeriods(24), []);
  const availableYears   = useMemo(() => [...new Set(generatedPeriods.map(p => p.year))].sort((a,b) => b-a), [generatedPeriods]);

  useEffect(() => { setSelectedLabel(`P${selectedP} ${selectedYear}`); }, [selectedP, selectedYear]);

  useEffect(() => {
    if (!selectedLabel) return;
    loadPeriod(selectedLabel);
  }, [selectedLabel]);

  async function loadPeriod(lbl) {
    setLoading(true);
    const gen = generatedPeriods.find(p => p.label === lbl);
    if (!gen) { setLoading(false); return; }

    const [
      { data: existing },
      { data: hoursData },
      { data: syncLog },
      { data: orderData },
      { data: teamData },
    ] = await Promise.all([
      supabase.from('tip_periods').select('*').eq('period_number', gen.period_number).eq('year', gen.year).single(),
      supabase.from('eitje_hours')
        .select('eitje_user_id, user_name, venue, team, hours_worked')
        .gte('date', gen.start_date).lte('date', gen.end_date)
        .eq('type', 'gewerkte_uren').eq('approved', true).limit(5000),
      supabase.from('eitje_sync_log').select('completed_at').eq('resource','hours').eq('status','completed').order('completed_at',{ascending:false}).limit(1),
      supabase.from('tip_member_order').select('eitje_user_id, sort_position'),
      supabase.from('team_members').select('eitje_user_id, nmbrs_employee_number, venue').not('eitje_user_id','is',null),
    ]);

    setLastSynced(syncLog?.[0]?.completed_at || null);

    const freshOrderMap = {};
    (orderData || []).forEach(r => { freshOrderMap[r.eitje_user_id] = r.sort_position; });
    setMemberOrder(freshOrderMap);

    const tmMap = {};
    (teamData || []).forEach(r => { tmMap[String(r.eitje_user_id)] = { nmbrs_employee_number: r.nmbrs_employee_number, tm_venue: r.venue }; });

    // Aggregate hours — track box_hours (all non-Sassys) and sassys_hours separately
    const map = {};
    (hoursData || []).forEach(row => {
      const key = row.eitje_user_id || row.user_name;
      const hrs = Number(row.hours_worked) || 0;
      if (!map[key]) {
        map[key] = { eitje_user_id: row.eitje_user_id, user_name: row.user_name, venue: row.venue, team: row.team, box_hours: 0, sassys_hours: 0, venueHours: {} };
      }
      if (row.venue === 'Sassys') {
        map[key].sassys_hours += hrs;
      } else {
        map[key].box_hours += hrs;
      }
      map[key].venueHours[row.venue] = (map[key].venueHours[row.venue] || 0) + hrs;
    });

    Object.values(map).forEach(m => {
      // Primary venue = most hours
      m.venue = Object.entries(m.venueHours).sort((a,b) => b[1]-a[1])[0]?.[0] || m.venue;
      delete m.venueHours;
      m.hours_worked = r2(m.box_hours + m.sassys_hours);
      // Overlay from team_members
      const tm = tmMap[String(m.eitje_user_id)];
      if (tm) {
        if (tm.tm_venue) m.venue = tm.tm_venue;
        m.nmbrs_employee_number = tm.nmbrs_employee_number ?? null;
      }
    });

    const VENUE_ORDER = { 'Box Jordaan':0, 'Box Plantage':1, 'Sassys':2, 'Prep Kitchen':3 };

    let savedMembers = [];
    if (existing?.id) {
      const { data: mems } = await supabase.from('tip_period_members').select('eitje_user_id, adjustment, adjustment_note, payout_method').eq('period_id', existing.id);
      savedMembers = mems || [];
    }
    const savedMap = {};
    savedMembers.forEach(m => { savedMap[m.eitje_user_id] = m; });

    const merged = Object.values(map)
      .filter(m => m.hours_worked > 0)
      .map(m => {
        const saved = savedMap[m.eitje_user_id] || {};
        return {
          ...m,
          box_hours:       r2(m.box_hours),
          sassys_hours:    r2(m.sassys_hours),
          hours_worked:    r2(m.hours_worked),
          box_tip_share:   0,
          sassys_tip_share:0,
          tip_share:       0,
          adjustment:      saved.adjustment      || 0,
          adjustment_note: saved.adjustment_note || '',
          payout_method:   saved.payout_method   || null,
          final_payout:    0,
        };
      })
      .sort((a, b) => {
        const va = VENUE_ORDER[a.venue] ?? 99;
        const vb = VENUE_ORDER[b.venue] ?? 99;
        if (va !== vb) return va - vb;
        return (a.nmbrs_employee_number ?? 99999) - (b.nmbrs_employee_number ?? 99999);
      });

    setPeriod(existing || {
      period_number: gen.period_number, year: gen.year,
      start_date: gen.start_date, end_date: gen.end_date,
      revenue_jordaan:0, revenue_plantage:0, revenue_sassys:0,
      tips_jordaan_cash:0, tips_jordaan_eft:0,
      tips_plantage_cash:0, tips_plantage_eft:0,
      tips_sassys_cash:0,
      total_hours:0, tips_per_hour:0,
      avg_tip_pct_jordaan:0, avg_tip_pct_plantage:0,
      locked:false,
    });
    setMembers(merged);
    setDirty(false);
    setLoading(false);
  }

  function handlePeriodChange(key, val) {
    setPeriod(p => ({ ...p, [key]: val }));
    setDirty(true);
  }

  // Two-pool calculation
  const calculated = useMemo(() => {
    const boxTips    = (period?.tips_jordaan_cash||0)+(period?.tips_jordaan_eft||0)+(period?.tips_plantage_cash||0)+(period?.tips_plantage_eft||0);
    const sassysTips = (period?.tips_sassys_cash||0);
    const totalBoxHours    = members.reduce((s,m) => s + (m.box_hours||0), 0);
    const totalSassysHours = members.reduce((s,m) => s + (m.sassys_hours||0), 0);
    const boxTph    = totalBoxHours    > 0 ? boxTips    / totalBoxHours    : 0;
    const sassysTph = totalSassysHours > 0 ? sassysTips / totalSassysHours : 0;
    return members.map(m => {
      const box_tip_share    = r2(boxTph    * (m.box_hours    || 0));
      const sassys_tip_share = r2(sassysTph * (m.sassys_hours || 0));
      const tip_share        = r2(box_tip_share + sassys_tip_share);
      const final_payout     = r2(tip_share + (m.adjustment || 0));
      return { ...m, box_tip_share, sassys_tip_share, tip_share, final_payout };
    });
  }, [period, members]);

  function handleMemberChange(idx, key, val) {
    if (period?.locked) return;
    setMembers(prev => { const u = [...prev]; u[idx] = { ...u[idx], [key]: val }; return u; });
    setDirty(true);
  }

  async function save() {
    if (!period) return;
    setSaving(true);
    try {
      const boxTips    = (period.tips_jordaan_cash||0)+(period.tips_jordaan_eft||0)+(period.tips_plantage_cash||0)+(period.tips_plantage_eft||0);
      const sassysTips = (period.tips_sassys_cash||0);
      const totalHours = calculated.reduce((s,m) => s+(m.hours_worked||0), 0);
      const periodRow  = {
        ...period,
        total_hours:    r2(totalHours),
        tips_per_hour:  r2(totalHours > 0 ? (boxTips+sassysTips)/totalHours : 0),
        avg_tip_pct_jordaan:  period.revenue_jordaan > 0 ? r2(((period.tips_jordaan_cash||0)+(period.tips_jordaan_eft||0))/period.revenue_jordaan*100) : 0,
        avg_tip_pct_plantage: period.revenue_plantage > 0 ? r2(((period.tips_plantage_cash||0)+(period.tips_plantage_eft||0))/period.revenue_plantage*100) : 0,
        updated_at: new Date().toISOString(),
      };
      let periodId = period.id;
      if (period.id) {
        await supabase.from('tip_periods').update(periodRow).eq('id', period.id);
      } else {
        const { data } = await supabase.from('tip_periods').insert(periodRow).select().single();
        periodId = data.id;
        setPeriod(p => ({ ...p, id: periodId }));
      }
      if (periodId && calculated.length > 0) {
        await supabase.from('tip_period_members').delete().eq('period_id', periodId);
        await supabase.from('tip_period_members').insert(
          calculated.map(m => ({
            period_id: periodId, eitje_user_id: m.eitje_user_id, user_name: m.user_name,
            venue: m.venue, team: m.team, hours_worked: m.hours_worked,
            tip_share: m.tip_share, adjustment: m.adjustment||0,
            adjustment_note: m.adjustment_note||null, payout_method: m.payout_method||null,
            final_payout: m.final_payout, updated_at: new Date().toISOString(),
          }))
        );
      }
      setDirty(false);
    } catch(e) { alert('Save error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function toggleLock() {
    if (!period?.id) { alert('Save the period first before locking.'); return; }
    const newLocked = !period.locked;
    await supabase.from('tip_periods').update({ locked: newLocked, locked_at: newLocked ? new Date().toISOString() : null }).eq('id', period.id);
    setPeriod(p => ({ ...p, locked: newLocked }));
  }

  function downloadXlsx() {
    const visible = calculated.filter(m => vf === 'All' || m.venue === vf);
    const rows = visible.map(m => ({
      Name:        m.user_name,
      Hours:       r2(m.hours_worked),
      'Box Tips':  r2(m.box_tip_share),
      'Sassy Tips':r2(m.sassys_tip_share),
      Tips:        r2(m.final_payout),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch:28 },{ wch:10 },{ wch:12 },{ wch:12 },{ wch:10 }];
    const wb = XLSX.utils.book_new();
    const venueLabel = vf==='All' ? 'All' : vf.replace('Box ','');
    XLSX.utils.book_append_sheet(wb, ws, `Tips ${selectedLabel}`);
    XLSX.writeFile(wb, `Tips_${selectedLabel.replace(' ','_')}_${venueLabel}.xlsx`);
  }

  if (!isAdmin) return (
    <div style={{ padding:'3rem', textAlign:'center', color:C.mid }}>
      <div style={{ fontSize:'32px', marginBottom:'12px' }}>🔒</div>
      <div style={{ fontSize:'16px', fontWeight:700, color:C.charcoal }}>Admin only.</div>
    </div>
  );

  const gen    = generatedPeriods.find(p => p.label === selectedLabel);
  const locked = period?.locked || false;

  // Table column config
  const COLS = '2fr 1fr 1fr 1.1fr 1.1fr 1.1fr 1.4fr 0.8fr 1.1fr';
  const HEADS = ['Name','Venue','Hours','Box Tips',"Sassy's Tips",'Total Tips','Adjustment','Pay Via','Final Payout'];

  return (
    <div style={{ fontFamily:'inherit' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'10px' }}>
        <div>
          <h3 style={{ margin:'0 0 2px', fontSize:'16px', fontWeight:800, color:C.charcoal }}>Tips Distribution</h3>
          <div style={{ fontSize:'12px', color:C.mid }}>
            Admin only · {gen ? `${gen.start_date} → ${gen.end_date}` : ''}
            {lastSynced && <span style={{ marginLeft:'10px', color:C.green, fontWeight:600 }}>✓ Synced {new Date(lastSynced).toLocaleString('en-NL',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</span>}
            {!lastSynced && <span style={{ marginLeft:'10px', color:C.amber, fontWeight:600 }}>⚠ Sync status unknown</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
          <select value={selectedP} onChange={e => setSelectedP(parseInt(e.target.value))}
            style={{ padding:'7px 12px', border:`1.5px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', fontWeight:600, color:C.charcoal, background:C.white, cursor:'pointer' }}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(p => <option key={p} value={p}>P{p}</option>)}
          </select>
          <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}
            style={{ padding:'7px 12px', border:`1.5px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', fontWeight:600, color:C.charcoal, background:C.white, cursor:'pointer' }}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          {locked
            ? <span style={{ padding:'4px 10px', borderRadius:'20px', background:C.greenLight, color:C.green, fontSize:'12px', fontWeight:700, border:`1px solid ${C.green}44` }}>🔒 Locked</span>
            : <span style={{ padding:'4px 10px', borderRadius:'20px', background:C.amberLight, color:C.amber, fontSize:'12px', fontWeight:700, border:`1px solid ${C.amber}44` }}>Unlocked</span>
          }
          <button onClick={toggleLock} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', border:`1px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.mid, fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
            {locked ? <><Unlock size={13}/> Unlock</> : <><Lock size={13}/> Lock Period</>}
          </button>
          {calculated.length > 0 && (
            <button onClick={downloadXlsx} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', border:`1px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.mid, fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
              <Download size={13}/> Export
            </button>
          )}
          {calculated.length > 0 && (
            <button onClick={() => window.print()} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', border:`1px solid ${C.border}`, borderRadius:'8px', background:C.white, color:C.mid, fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
              <Printer size={13}/> Print
            </button>
          )}
          {!locked && dirty && (
            <button onClick={save} disabled={saving} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', border:'none', borderRadius:'8px', background:C.navy, color:C.white, fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
              <Save size={13}/> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding:'60px', textAlign:'center', color:C.mid, fontSize:'14px' }}>Loading period…</div>
      ) : (
        <>
          {period && <SummaryCard period={period} members={calculated} locked={locked} onChange={handlePeriodChange}/>}

          {/* Venue filter */}
          {calculated.length > 0 && (
            <div style={{ display:'flex', gap:'6px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center' }}>
              {['All','Box Jordaan','Box Plantage','Sassys','Prep Kitchen'].map(v => (
                <button key={v} onClick={() => setVf(v)} style={{ padding:'4px 12px', borderRadius:'20px', border:'none', background: vf===v ? C.navy : C.cream, color: vf===v ? C.white : C.mid, fontSize:'12px', fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>
                  {v === 'All' ? 'All Venues' : v.replace('Box ','')}
                </button>
              ))}
            </div>
          )}

          {/* Member table */}
          {calculated.length === 0 ? (
            <div style={{ padding:'40px', textAlign:'center', color:C.mid, fontSize:'13px', background:C.cream, borderRadius:'12px', border:`1px solid ${C.border}` }}>
              No hours data found for this period.
            </div>
          ) : (
            <div style={{ borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden' }}>
              {/* Header */}
              <div style={{ display:'grid', gridTemplateColumns:COLS, background:C.cream, borderBottom:`2px solid ${C.border}` }}>
                {HEADS.map((h, i) => (
                  <div key={i} style={{ padding:'10px 12px', fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', textAlign: i>=2?'center':'left' }}>{h}</div>
                ))}
              </div>

              {calculated.filter(m => vf==='All' || m.venue===vf).map((m, idx) => (
                <div key={m.eitje_user_id||m.user_name} style={{ display:'grid', gridTemplateColumns:COLS, borderBottom:`1px solid ${C.border}`, background: idx%2===0?C.white:'#FAFAF8', alignItems:'center' }}>
                  {/* Name */}
                  <div style={{ padding:'10px 12px' }}>
                    <div style={{ fontWeight:700, fontSize:'13px', color:C.charcoal }}>{m.user_name}</div>
                    {m.nmbrs_employee_number && <div style={{ fontSize:'10px', color:C.mid, marginTop:'1px' }}>#{m.nmbrs_employee_number}</div>}
                  </div>
                  {/* Venue */}
                  <div style={{ padding:'10px 12px', fontSize:'12px', color:C.mid }}>{m.venue?.replace('Box ','') || '—'}</div>
                  {/* Hours */}
                  <div style={{ padding:'10px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color:C.charcoal }}>{Number(m.hours_worked).toFixed(2)}h</span>
                    {m.sassys_hours > 0 && m.box_hours > 0 && (
                      <div style={{ fontSize:'10px', color:C.mid }}>
                        {m.box_hours.toFixed(1)}b · {m.sassys_hours.toFixed(1)}s
                      </div>
                    )}
                  </div>
                  {/* Box tips */}
                  <div style={{ padding:'10px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color: m.box_tip_share > 0 ? C.skyText : C.border }}>
                      {m.box_tip_share > 0 ? fmt(m.box_tip_share) : '—'}
                    </span>
                  </div>
                  {/* Sassys tips */}
                  <div style={{ padding:'10px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color: m.sassys_tip_share > 0 ? C.purple : C.border }}>
                      {m.sassys_tip_share > 0 ? fmt(m.sassys_tip_share) : '—'}
                    </span>
                  </div>
                  {/* Total tip share */}
                  <div style={{ padding:'10px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'13px', fontWeight:700, color:C.green }}>{fmt(m.tip_share)}</span>
                  </div>
                  {/* Adjustment */}
                  <div style={{ padding:'6px 12px' }}>
                    <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                      <span style={{ fontSize:'12px', color:C.mid, flexShrink:0 }}>€</span>
                      <input type="number" step="0.01" value={m.adjustment||''} disabled={locked}
                        onChange={e => handleMemberChange(idx,'adjustment',parseFloat(e.target.value)||0)}
                        placeholder="0.00" style={{ ...iStyle(locked), padding:'5px 7px', fontSize:'12px', width:'70px' }}/>
                      <input type="text" value={m.adjustment_note||''} disabled={locked}
                        onChange={e => handleMemberChange(idx,'adjustment_note',e.target.value)}
                        placeholder="Note…" style={{ ...iStyle(locked), padding:'5px 7px', fontSize:'11px', flex:1, textAlign:'left' }}/>
                    </div>
                  </div>
                  {/* Pay via */}
                  <div style={{ padding:'6px 12px', display:'flex', justifyContent:'center' }}>
                    <div style={{ display:'flex', gap:'2px', background:C.cream, borderRadius:'6px', padding:'2px' }}>
                      {['cash','eft'].map(method => (
                        <button key={method} disabled={locked}
                          onClick={() => handleMemberChange(idx,'payout_method', m.payout_method===method ? null : method)}
                          style={{ padding:'4px 10px', border:'none', borderRadius:'4px', fontSize:'11px', fontWeight:700, cursor:locked?'not-allowed':'pointer', transition:'all 0.1s',
                            background: m.payout_method===method ? (method==='cash'?C.amberLight:C.skyBlueLight) : 'transparent',
                            color:      m.payout_method===method ? (method==='cash'?C.amber:C.skyText)           : C.mid,
                          }}>
                          {method.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Final payout */}
                  <div style={{ padding:'10px 12px', textAlign:'center' }}>
                    <span style={{ fontSize:'14px', fontWeight:800, color: m.adjustment>0?C.green:m.adjustment<0?C.red:C.charcoal }}>
                      {fmt(m.final_payout)}
                    </span>
                    {m.adjustment !== 0 && (
                      <div style={{ fontSize:'10px', color: m.adjustment>0?C.green:C.red, fontWeight:600 }}>
                        {m.adjustment>0?'+':''}{fmt(m.adjustment)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Totals */}
              {(() => {
                const vis        = calculated.filter(m => vf==='All' || m.venue===vf);
                const totalCash  = vis.filter(m=>m.payout_method==='cash').reduce((s,m)=>s+(m.final_payout||0),0);
                const totalEft   = vis.filter(m=>m.payout_method==='eft').reduce((s,m)=>s+(m.final_payout||0),0);
                const unassigned = vis.filter(m=>!m.payout_method).reduce((s,m)=>s+(m.final_payout||0),0);
                return (
                  <>
                    <div style={{ display:'grid', gridTemplateColumns:COLS, background:C.cream, borderTop:`2px solid ${C.border}` }}>
                      <div style={{ padding:'12px', fontSize:'12px', fontWeight:700, color:C.charcoal, gridColumn:'1/3' }}>TOTAL</div>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'13px', fontWeight:700, color:C.charcoal }}>
                        {vis.reduce((s,m)=>s+(m.hours_worked||0),0).toFixed(2)}h
                      </div>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'13px', fontWeight:700, color:C.skyText }}>
                        {fmt(vis.reduce((s,m)=>s+(m.box_tip_share||0),0))}
                      </div>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'13px', fontWeight:700, color:C.purple }}>
                        {fmt(vis.reduce((s,m)=>s+(m.sassys_tip_share||0),0))}
                      </div>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'13px', fontWeight:700, color:C.green }}>
                        {fmt(vis.reduce((s,m)=>s+(m.tip_share||0),0))}
                      </div>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'12px', color:C.mid }}>
                        {fmt(vis.reduce((s,m)=>s+(m.adjustment||0),0))}
                      </div>
                      <div/>
                      <div style={{ padding:'12px', textAlign:'center', fontSize:'14px', fontWeight:800, color:C.navy }}>
                        {fmt(vis.reduce((s,m)=>s+(m.final_payout||0),0))}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'10px', padding:'10px 14px', background:C.cream, borderTop:`1px solid ${C.border}`, flexWrap:'wrap' }}>
                      <div style={{ padding:'6px 14px', borderRadius:'8px', background:C.amberLight, border:`1px solid ${C.amber}44` }}>
                        <span style={{ fontSize:'11px', fontWeight:700, color:C.amber, marginRight:'8px' }}>CASH</span>
                        <span style={{ fontSize:'14px', fontWeight:800, color:C.amber }}>{fmt(totalCash)}</span>
                      </div>
                      <div style={{ padding:'6px 14px', borderRadius:'8px', background:C.skyBlueLight, border:`1px solid ${C.skyText}44` }}>
                        <span style={{ fontSize:'11px', fontWeight:700, color:C.skyText, marginRight:'8px' }}>EFT</span>
                        <span style={{ fontSize:'14px', fontWeight:800, color:C.skyText }}>{fmt(totalEft)}</span>
                      </div>
                      {unassigned > 0 && (
                        <div style={{ padding:'6px 14px', borderRadius:'8px', background:C.cream, border:`1px solid ${C.border}` }}>
                          <span style={{ fontSize:'11px', fontWeight:700, color:C.mid, marginRight:'8px' }}>UNASSIGNED</span>
                          <span style={{ fontSize:'14px', fontWeight:800, color:C.mid }}>{fmt(unassigned)}</span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .tips-print-area { display: none; }
        @media print {
          body * { visibility: hidden; }
          .tips-print-area, .tips-print-area * { visibility: visible; display: block; }
          .tips-print-area { position: fixed; top: 0; left: 0; width: 100%; padding: 24px; display: block !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Print area */}
      <div className="tips-print-area">
        <div style={{ marginBottom:'16px' }}>
          <div style={{ fontSize:'18px', fontWeight:800 }}>Tips Distribution — {selectedLabel}</div>
          <div style={{ fontSize:'12px', color:'#666' }}>{gen ? `${gen.start_date} → ${gen.end_date}` : ''} · {vf !== 'All' ? vf : 'All Venues'}</div>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
          <thead>
            <tr style={{ borderBottom:'2px solid #333' }}>
              {['Name','Venue','Hours','Box Tips',"Sassy's Tips",'Total Tips','Adjustment','Final Payout','Pay Via'].map(h => (
                <th key={h} style={{ textAlign: h==='Name'||h==='Venue'?'left':'right', padding:'6px 8px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calculated.filter(m => vf==='All' || m.venue===vf).map((m, i) => (
              <tr key={m.eitje_user_id||m.user_name} style={{ borderBottom:'1px solid #ddd', background: i%2===0?'#fff':'#f9f9f9' }}>
                <td style={{ padding:'5px 8px', fontWeight:600 }}>{m.user_name}</td>
                <td style={{ padding:'5px 8px', color:'#666' }}>{m.venue?.replace('Box ','') || '—'}</td>
                <td style={{ padding:'5px 8px', textAlign:'right' }}>{Number(m.hours_worked).toFixed(2)}h</td>
                <td style={{ padding:'5px 8px', textAlign:'right' }}>{m.box_tip_share > 0 ? fmt(m.box_tip_share) : '—'}</td>
                <td style={{ padding:'5px 8px', textAlign:'right' }}>{m.sassys_tip_share > 0 ? fmt(m.sassys_tip_share) : '—'}</td>
                <td style={{ padding:'5px 8px', textAlign:'right' }}>{fmt(m.tip_share)}</td>
                <td style={{ padding:'5px 8px', textAlign:'right' }}>{m.adjustment ? fmt(m.adjustment) : '—'}</td>
                <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:700 }}>{fmt(m.final_payout)}</td>
                <td style={{ padding:'5px 8px', textAlign:'center', textTransform:'uppercase', fontSize:'11px' }}>{m.payout_method || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid #333', fontWeight:800 }}>
              <td colSpan={2} style={{ padding:'6px 8px' }}>TOTAL</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.hours_worked||0),0).toFixed(2)}h</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmt(calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.box_tip_share||0),0))}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmt(calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.sassys_tip_share||0),0))}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmt(calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.tip_share||0),0))}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmt(calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.adjustment||0),0))}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmt(calculated.filter(m=>vf==='All'||m.venue===vf).reduce((s,m)=>s+(m.final_payout||0),0))}</td>
              <td/>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}